import { useState } from 'react';
import { useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select, Switch } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { dados } from '@/data/porta';
import type { CanalDeLembrete, Lembrete } from '@/data/tipos';

const CANAIS: { value: CanalDeLembrete; label: string }[] = [
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'email', label: 'E-mail' },
    { value: 'ambos', label: 'WhatsApp e e-mail' },
];

/** "1440 minutos" não se lê; "1 dia" sim. */
function antecedencia(minutos: number): string {
    if (minutos === 0) return 'no momento da confirmação';
    if (minutos % 1440 === 0) return `${minutos / 1440} ${minutos === 1440 ? 'dia' : 'dias'} antes`;
    if (minutos % 60 === 0) return `${minutos / 60} ${minutos === 60 ? 'hora' : 'horas'} antes`;
    return `${minutos} minutos antes`;
}

const VAZIO = { quando: '', descricao: '', canal: 'whatsapp' as CanalDeLembrete, ativo: true, offsetMin: 1440 };

/**
 * A régua de lembretes do agendamento.
 *
 * É a mesma lista que o modal do agendamento mostra: quem edita aqui muda o que
 * a recepção vê lá. Nenhuma linha dispara sozinha — não há canal pareado nem
 * agendador —, e a tela diz isso em vez de deixar a clínica contar com um
 * lembrete que não sai.
 */
export function ReguaDeLembretes({ lembretes, canalConectado }: { lembretes: Lembrete[]; canalConectado: boolean }) {
    const revalidator = useRevalidator();
    const aviso = useAviso();

    const [emEdicao, setEmEdicao] = useState<Lembrete | 'novo' | null>(null);
    const [form, setForm] = useState(VAZIO);
    const [salvando, setSalvando] = useState(false);
    const formSujo = useAlteracaoPendente(form, emEdicao);
    const [confirmar, confirmacao] = useConfirmacao();

    const abrir = (lembrete: Lembrete | 'novo') => {
        setEmEdicao(lembrete);
        setForm(lembrete === 'novo'
            ? VAZIO
            : {
                quando: lembrete.quando, descricao: lembrete.descricao,
                canal: lembrete.canal, ativo: lembrete.ativo, offsetMin: lembrete.offsetMin,
            });
    };

    const salvar = async () => {
        if (!emEdicao) return;
        setSalvando(true);
        try {
            await dados().salvarLembrete(emEdicao === 'novo' ? null : emEdicao.id, form);
            aviso({ title: emEdicao === 'novo' ? 'Lembrete criado' : 'Lembrete salvo', description: form.quando });
            setEmEdicao(null);
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível salvar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const remover = async (lembrete: Lembrete) => {
        const ok = await confirmar({
            titulo: `Remover o lembrete "${lembrete.quando}"?`,
            descricao: 'A régua da clínica deixa de ter este lembrete, para todos os agendamentos.',
            confirmar: 'Remover lembrete',
        });
        if (!ok) return;
        try {
            await dados().removerLembrete(lembrete.id);
            aviso({ title: 'Lembrete removido', description: lembrete.quando });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível remover', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    const colunas: DataTableColumn<Lembrete>[] = [
        {
            key: 'quando', header: 'Quando',
            render: (l) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{l.quando}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{antecedencia(l.offsetMin)}</span>
                </span>
            ),
        },
        { key: 'descricao', header: 'Mensagem' },
        {
            key: 'canal', header: 'Canal', width: 150,
            render: (l) => <Badge tone="neutral">{CANAIS.find((c) => c.value === l.canal)?.label ?? l.canal}</Badge>,
        },
        {
            key: 'ativo', header: 'Situação', width: 120,
            render: (l) => <Badge tone={l.ativo ? 'success' : 'neutral'}>{l.ativo ? 'Ativo' : 'Desligado'}</Badge>,
        },
        {
            key: 'acoes', header: '', width: 170, align: 'right',
            render: (l) => (
                <span style={{ display: 'inline-flex', gap: 6 }}>
                    <Button size="sm" variant="secondary" onClick={() => abrir(l)}>Editar</Button>
                    <Button size="sm" variant="ghost" onClick={() => remover(l)}>Remover</Button>
                </span>
            ),
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Alert tone="warning" dismissible={false} title="A régua está declarada, mas nada é disparado ainda">
                {canalConectado
                    ? 'Falta o agendador que chama a régua na hora certa.'
                    : 'Nenhum canal está pareado e não há agendador — o lembrete não teria por onde sair nem quem o chamasse.'}
                {' '}Enquanto isso, a régua é o que a recepção vê no modal do agendamento.
            </Alert>

            <Card
                title="Régua de lembretes"
                subtitle="Do mais distante do atendimento para o mais próximo"
                padding={0}
                actions={(
                    <Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => abrir('novo')}>
                        Novo lembrete
                    </Button>
                )}
            >
                <DataTable
                    columns={colunas} rows={lembretes} rowKey="id"
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhum lembrete configurado."
                />
            </Card>

            {confirmacao}

            <Modal
                open={emEdicao !== null}
                title={emEdicao === 'novo' ? 'Novo lembrete' : 'Editar lembrete'}
                onClose={() => setEmEdicao(null)}
                dirty={formSujo}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setEmEdicao(null)}>Cancelar</Button>
                        <Button onClick={salvar} disabled={!form.quando.trim() || salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </Button>
                    </>
                )}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <FormField label="Rótulo" htmlFor="lembrete-quando" hint="O que a recepção lê na lista: “1 dia antes”." required>
                        <Input id="lembrete-quando" value={form.quando} onChange={(v) => setForm({ ...form, quando: v })} />
                    </FormField>
                    <FormField label="Antecedência, em minutos" htmlFor="lembrete-offset" hint="Zero envia no momento da confirmação.">
                        <Input
                            id="lembrete-offset" type="number" value={String(form.offsetMin)}
                            onChange={(v) => setForm({ ...form, offsetMin: Math.max(0, Number(v) || 0) })}
                        />
                    </FormField>
                    <FormField label="Mensagem" htmlFor="lembrete-descricao">
                        <Input id="lembrete-descricao" value={form.descricao} onChange={(v) => setForm({ ...form, descricao: v })} />
                    </FormField>
                    <FormField label="Canal" htmlFor="lembrete-canal">
                        <Select id="lembrete-canal" value={form.canal} onChange={(v) => setForm({ ...form, canal: v as CanalDeLembrete })} options={CANAIS} />
                    </FormField>
                    <Switch checked={form.ativo} onChange={(v) => setForm({ ...form, ativo: v })} label="Ativo" />
                </div>
            </Modal>
        </div>
    );
}
